import { Component, computed, inject, Injectable, OnInit, signal } from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MatOptionModule } from "@angular/material/core";
import { Data, Params } from "@angular/router";
import { finalize, Observable, of } from "rxjs";
import { IModalComponent } from "../../../components/direct-modal-link/Types/IModalComponent";
import { CurrentUserService } from "../../../shared/CurrentUser.service";
import { AppDateAdapter, APP_DATE_FORMATS } from "../../../shared/format-datepicker";
import { FormControlHelper } from "../../../shared/helpers/FormControlHelper";
import { ChangeDetectionService } from "../../../shared/services/change-detection.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { LoadingService } from "../../../shared/services/loading-overlay/loading-overlay.component";
import { InvoiceModalService } from "../invoice-modal.service";
import { GetInvoiceStatusDescription, Invoice, InvoiceStatus, InvoiceUpdate } from "../types/Invoice";
import { Trial as InvoiceAssocTrial, InvoicePaymentLineItemAssocComponent } from "../payment-line-item-assoc/invoice-payment-line-item-assoc.component";
import * as InvoiceAssocMapper from "./assoc-mapper";
import { PossibleInvoicePaymentLineItems } from "../types/PossibleInvoicePaymentLineItems";
import { AppRoles } from "../../../shared/enums/AppRoles";
import { YesNoModal } from "../../yes-no-modal/yes-no-modal.component";
import { VendorDetailModal } from "../../vendor-detail-modal/vendor-detail-modal.component";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { CommonModule } from "@angular/common";
import { DocumentPickerComponent } from "../../../components/document-picker/document-picker.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { YesNoPipe } from "../../../shared/pipes/yes-no.pipe";
import { ScrollInsetShadowDirective } from "../../../directives/scroll-inset-shadow.directive";

interface ShowInvoiceEditModalData {
  InvoiceId: number;
  isFromVendor?: boolean;
}

@Component({
  selector: 'invoice-edit-modal',
  templateUrl: 'invoice-edit-modal.component.html',
  styleUrl: 'invoice-edit-modal.component.less',
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    LoadingService, ChangeDetectionService, DialogService, InvoiceModalService
  ],
  standalone: true,
  imports: [FormsModule, MatDialogModule, CommonModule, DocumentPickerComponent, MatFormFieldModule, InvoicePaymentLineItemAssocComponent,
    MatInputModule, MatDatepickerModule, MatButtonModule, MatSelectModule, MatOptionModule, YesNoPipe, MatSnackBarModule, ScrollInsetShadowDirective]
})
export class InvoiceEditModalComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<InvoiceEditModal, number>>(MatDialogRef);
  private showModalData = inject<ShowInvoiceEditModalData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private loadingService = inject(LoadingService)
  private formControlHelper = inject(FormControlHelper);
  private userService = inject(CurrentUserService);
  private changeDetectionService = inject(ChangeDetectionService);
  private snackbar = inject(MatSnackBar);
  private invoiceService = inject(InvoiceModalService);
  private vendorDetailModal = inject(VendorDetailModal);

  private AssocData: Array<InvoiceAssocTrial> = [];
  private UserRegionalAdminCountries: number[] = [];
  private isLoadingDocument: boolean = false;

  protected Invoice = signal<Invoice>(null);
  protected isComplete = computed(() => this.Invoice()?.InvoiceStatus === InvoiceStatus.Complete);
  protected isNewStatus = computed(() => this.Invoice()?.InvoiceStatus === InvoiceStatus.New);
  protected isPurchaseOrderInvoice = signal(false);
  protected isPurchaseRequestInvoice = signal(false);
  protected UserIsAdmin: boolean = false;
  protected isFromVendor?: boolean = this.showModalData.isFromVendor;
  protected cols: { [key: string]: { $implicit: string } } = {}; // Create an object of bootstrap col-sm-N class names
  protected TimelineCalc: number = null;
  protected PoEnd = null;
  protected Dropdowns = {
    InvoiceStatuses: [InvoiceStatus.New, InvoiceStatus.Submitted, InvoiceStatus.Complete].map((a) => ({
      id: a,
      label: GetInvoiceStatusDescription(a),
      isEnabled: () => true
    }))
  }

  ngOnInit() {
    [...Array(12).keys()].map((a) => {
      this.cols[`sm${a}`] = { $implicit: `col-sm-${a}` };
    });

    const loadRef = this.loadingService.StartWaiting("Fetching data");
    this.invoiceService.GetInvoice(this.showModalData.InvoiceId)
      .pipe(finalize(() => loadRef.Stop()))
      .subscribe((invoice) => {
        this.invoiceService.GetPossiblePaymentLineItems(invoice.Id).subscribe((possiblePaymentLineItems) => {
          this.SetInvoiceData(invoice, possiblePaymentLineItems);

          this.userService.HasRole(AppRoles.PaymentAdmin).subscribe((hasRole) => {
            this.UserIsAdmin = this.UserIsAdmin || hasRole;
            this.userService.GetPaymentAdminCountries().subscribe((countries) => {
              this.UserRegionalAdminCountries = countries;
              const isRegionalAdmin = this.UserRegionalAdminCountries.find((c) => c == this.Invoice().VendorCountryId) != null;
              this.UserIsAdmin = this.UserIsAdmin || isRegionalAdmin;
            });
          });
        });
      });
  }

  protected isDocumentChangeDisabled = (): boolean => !this.isNewStatus();

  private SetInvoiceData(invoice: Invoice, possiblePaymentLineItems: Array<PossibleInvoicePaymentLineItems>) {
    this.Invoice.set(invoice);
    this.AssocData = InvoiceAssocMapper.MapApiToAssoc(possiblePaymentLineItems, this.Invoice().Id);

    this.isPurchaseOrderInvoice.set(invoice.PurchaseOrderNumber != null && invoice.PurchaseOrderNumber.length > 0);
    this.isPurchaseRequestInvoice.set(invoice.PurchaseRequisitionNumber != null && invoice.PurchaseRequisitionNumber.length > 0);

    if (invoice.QuotePurchaseOrderEndDates?.length == 1)
      this.PoEnd = invoice.QuotePurchaseOrderEndDates[0];
    if (invoice.QuotePurchaseOrderEndDates?.length > 1)
      this.PoEnd = "Multiple";

    if (invoice.VendorInvoiceDate != null && invoice.DueDate != null) {
      const dueDate = new Date(invoice.DueDate);
      const invoiceDate = new Date(invoice.VendorInvoiceDate);

      if(invoiceDate <= dueDate)
        this.TimelineCalc = Math.trunc((dueDate.getTime() - invoiceDate.getTime()) / (1000 * 3600 * 24));
    }

    this.changeDetectionService.SetOriginalValue(this.GetChangeDetectionData());
  }

  public SaveAndClose = (form: NgForm) => this.save(form, true);
  public Save = (form: NgForm) => this.save(form, false);

  private save(form: NgForm, closeOnSave: boolean) {
    if (!this.formControlHelper.CheckFormIsValid(form))
      return;

    if (this.isLoadingDocument == true) {
      this.snackbar.open("Document must finish uploading before saving.", null, {
        duration: 5000,
      });
      return;
    }

    const updateData: InvoiceUpdate = this.Invoice();
    updateData.PaymentLineItemInvoiceAssocUpdates = InvoiceAssocMapper.MapAssocToUpdates(this.AssocData, this.Invoice().Id);
    updateData.DocumentIds = this.Invoice().Documents.map(a => a.Id);

    const loadRef = this.loadingService.StartWaiting("Updating invoice");
    this.invoiceService.UpdateInvoice(this.Invoice().Id, updateData)
      .pipe(finalize(() => loadRef.Stop()))
      .subscribe((updatedInvoice) => {
        if (closeOnSave) {
          this.dialogRef.close(updatedInvoice.Id);
        } else {
          this.invoiceService.GetPossiblePaymentLineItems(updatedInvoice.Id).subscribe((possiblePaymentLineItems) => {
            this.SetInvoiceData(updatedInvoice, possiblePaymentLineItems);
          });
        }
      })
  }

  protected CancelModal = () => {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;
      this.dialogRef.close(undefined);
    });
  }

  protected DisableDelete = () => 
    this.Invoice()?.CanBeDeleted == null || !this.Invoice().CanBeDeleted || this.changeDetectionService.HasChanges(this.GetChangeDetectionData())

  protected DeleteInvoice = () => {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;

      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        headerTitle: "Delete Invoice", message: "Are you sure you want to delete this Invoice?",
        yesText: "Yes",
        noText: "No"
      };
      this.dialog.open(YesNoModal, dialogConfig).afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          const loadingRef = this.loadingService.StartWaiting("Deleting Invoice");
          this.invoiceService.DeleteInvoice(this.Invoice().Id)
            .pipe(finalize(() => loadingRef.Stop()))
            .subscribe(() => {
              this.snackbar.open("Invoice deleted successfully.", null, {
                duration: 5000,
              });
              this.dialogRef.close(null);
            });
        }
      });
    });
  }

  protected onSafeToSaveParent(safe: boolean) {
    this.isLoadingDocument = !safe;
  }

  private GetChangeDetectionData = () => ({
    invoice: this.Invoice(),
    assocUpdates: InvoiceAssocMapper.MapAssocToUpdates(this.AssocData, this.Invoice().Id)
  })

  private CheckForChangesAndPrompt(): Observable<boolean> {
    if (this.changeDetectionService.HasChanges(this.GetChangeDetectionData())) {
      return this.changeDetectionService.HasChangeDialog();
    }
    return of(true);
  }

  protected editVendor(vendorId: number) {
    if (!this.isFromVendor) {
      this.vendorDetailModal.ShowModal({
        vendorId: vendorId,
        isFromInvoice: true
      });
    }
  }
}

@Injectable()
export class InvoiceEditModal implements IModalComponent {
  private dialog = inject(MatDialog);

  public ShowStandaloneModal = (_data: Data, params: Params): Observable<any> => this.ShowModal({
    InvoiceId: Number(params.get('invoiceId')),
  });

  public ShowModal = (data: ShowInvoiceEditModalData): Observable<number | undefined> => {
    const ref = this.dialog.open(InvoiceEditModalComponent, {
      data: data,
      width: "1500px",
      disableClose: true,
      autoFocus: false,
      panelClass: ["invoice-edit-modal-panel", "standard-modal-panel"],
    });

    return ref.afterClosed();
  }
}
